import { useState, useEffect, useRef } from 'react';
import { useEventCallback } from '../../hooks/use-event-callback';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ordersAPI, couriersAPI } from '../../lib/api';
import { fetchWithCache } from '../../lib/cache';
import { toast } from 'sonner';
import { Search, Eye, FileText, Printer, Package, RefreshCcw, Store, ShoppingBag, Pencil } from 'lucide-react';
import ShippingLabel from '../../components/ShippingLabel';
import OrderItemFilters, { useOrderItemFilters } from '../../components/OrderItemFilters';

export default function AdminOrders() {
  const itemFilters = useOrderItemFilters();
  const requestRef = useRef(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [orderTypeTab, setOrderTypeTab] = useState('pos'); // New tab state
  const [gstFilter, setGstFilter] = useState(searchParams.get('gst_applied') || 'all');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updateData, setUpdateData] = useState({ status: '', tracking_number: '', courier_provider: '', notes: '' });
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [labelOrder] = useState(null);
  const [settings] = useState(null);
  const labelRef = useRef(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(20);

  const fetchData = useEventCallback(async (pageNum = page) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        limit,
        search: search || undefined,
        source: orderTypeTab === 'pos' ? 'pos' : 'wholesale', // Filter by order type
        exclude_online: true // Exclude online customer orders
      };
      if (paymentFilter !== 'all') params.payment_method = paymentFilter;
      if (gstFilter !== 'all') params.gst_applied = gstFilter === 'true';

      const couriersPromise = couriersAPI.getAll().catch(() => ({ data: [] }));

      // Fetch orders with cache
      const cacheKey = `admin_orders_${orderTypeTab}_${paymentFilter}_${gstFilter}_${search}_${pageNum}_${limit}`;

      const ordersPromise = fetchWithCache(
        cacheKey,
        async () => {
          const res = await itemFilters.fetchOrders(params);
          return res.data;
        },
        (data, isFromCache) => {
          if (data && requestId === requestRef.current) {
            setOrders(data.orders || []);
            setTotalPages(data.pages || 1);
          }
        }
      );

      const [, couriersRes] = await Promise.all([ordersPromise, couriersPromise]);
      if (requestId === requestRef.current) setCouriers(couriersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      if (requestId === requestRef.current) { setOrders([]); toast.error('Failed to load filtered orders'); }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  });

  // Search is debounced; payment/type filters retain immediate refresh behavior.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchData(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, itemFilters.itemSearch, itemFilters.categories.category, itemFilters.categories.subcategory, itemFilters.categories.categoryIds, fetchData]);

  useEffect(() => {
    setPage(1);
    fetchData(1);
  }, [paymentFilter, orderTypeTab, gstFilter, fetchData]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setUpdateData({
      status: order.status,
      tracking_number: order.tracking_number || '',
      courier_provider: order.courier_provider || '',
      notes: '',
    });
    setShowDialog(true);
  };

  const handleUpdateStatus = async () => {
    try {
      await ordersAPI.updateStatus(selectedOrder.id, updateData);
      toast.success('Order updated successfully');
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };


  const handleConfirmPayment = async (order) => {
    if (!window.confirm(`Confirm payment received for Order #${order.order_number}?`)) return;
    try {
      await ordersAPI.updateStatus(order.id, {
        status: 'confirmed',
        payment_status: 'paid'
      });
      toast.success('Payment confirmed');
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm payment');
    }
  };

  const handlePrintInvoice = async (orderId) => {
    try {
      const response = await ordersAPI.getInvoice(orderId);

      // Create blob from response data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // Fallback: download the file
        const link = document.createElement('a');
        link.href = url;
        link.download = `Invoice_${orderId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Invoice error:', error);
      toast.error('Failed to generate invoice');
    }
  };

  const handlePrintLabel = async (orderId) => {
    try {
      const response = await ordersAPI.getShippingLabel(orderId);

      // Create blob from response data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // Fallback: download the file
        const link = document.createElement('a');
        link.href = url;
        link.download = `ShippingLabel_${orderId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Shipping label error:', error);
      toast.error('Failed to generate shipping label');
    }
  };

  const handlePrintShippingLabel = () => {
    if (!labelRef.current) return;
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the shipping label');
      return;
    }
    printWindow.document.write(`<!doctype html><html><head><title>Shipping Label</title><style>body{margin:0;padding:16px;font-family:Arial,sans-serif}@media print{body{padding:0}}</style></head><body>${labelRef.current.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };



  const getStatusBadgeClass = (status) => {
    const classes = {
      active: 'bg-green-500/20 text-green-400',
      pending: 'bg-amber-500/20 text-amber-400',
      processing: 'bg-blue-500/20 text-blue-400',
      shipped: 'bg-indigo-500/20 text-indigo-400',
      delivered: 'bg-emerald-500/20 text-emerald-400',
      cancelled: 'bg-red-500/20 text-red-400',
      returned: 'bg-rose-500/20 text-rose-400',
      partial_returned: 'bg-orange-500/20 text-orange-400',
      pending_payment: 'bg-orange-500/20 text-orange-400',
    };
    return classes[status] || 'bg-slate-500/20 text-slate-400';
  };

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">POS / Wholesale Orders</h1>
          <p className="text-slate-400">{orders.length} orders</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              setRefreshing(true);
              await fetchData();
              setRefreshing(false);
            }}
            className="border-slate-600 hover:bg-slate-700"
          >
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm("This will scan all orders and update statuses based on return history. Continue?")) return;
              try {
                setRefreshing(true);
                const res = await ordersAPI.recalculateStatuses();
                toast.success(res.data.message);
                await fetchData();
              } catch (err) {
                toast.error("Failed to sync statuses");
              } finally {
                setRefreshing(false);
              }
            }}
            className="border-slate-600 hover:bg-slate-700 text-orange-400"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Sync Status
          </Button>
        </div>
      </div>

      {/* Order Type Tabs */}
      <OrderItemFilters filters={itemFilters} onClear={() => { setSearch(''); setPaymentFilter('all'); setGstFilter('all'); }} />
      <Tabs value={orderTypeTab} onValueChange={setOrderTypeTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="pos" className="flex items-center gap-2">
            <Store className="w-4 h-4" />
            POS Orders
          </TabsTrigger>
          <TabsTrigger value="wholesale" className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" />
            Wholesale Orders
          </TabsTrigger>
        </TabsList>

        <TabsContent value={orderTypeTab} className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            {/* Payment Filter */}
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700">
                <SelectValue placeholder="Payment Mode" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Payment Modes</SelectItem>
                <SelectItem value="cod">Cash on Delivery</SelectItem>
                <SelectItem value="cash">Cash (POS)</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="online">Online</SelectItem>
              </SelectContent>
            </Select>

            {/* GST Filter */}
            <Select value={gstFilter} onValueChange={setGstFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700">
                <SelectValue placeholder="GST Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">GST Applied</SelectItem>
                <SelectItem value="false">No GST</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by order #, phone, or customer name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 input-admin"
              />
            </div>
          </div>

          {/* Orders Table */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Order #</TableHead>
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">Customer</TableHead>
                    <TableHead className="text-slate-400">Items</TableHead>
                    <TableHead className="text-slate-400">Total</TableHead>
                    <TableHead className="text-slate-400">Payment</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                    </TableRow>
                  ) : orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                        <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        No {orderTypeTab} orders found
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow key={order.id} className="border-slate-700">
                        <TableCell className="font-mono font-medium">{order.order_number}</TableCell>
                        <TableCell className="text-slate-400">
                          {new Date(order.created_at).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell>{order.customer_name || order.customer_phone || '-'}</TableCell>
                        <TableCell>{order.items.length} items</TableCell>
                        <TableCell className="font-semibold">₹{order.grand_total.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize border-slate-600">
                            {order.payment_method}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeClass(order.status)}>
                            {order.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 flex-nowrap">
                            <Button size="icon" variant="ghost" onClick={() => handleViewOrder(order)} title="View">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              title="Edit in POS"
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10"
                              onClick={() => navigate(`/admin/pos?edit=${order.id}`)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handlePrintInvoice(order.id)} title="Invoice">
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handlePrintLabel(order.id)} title="Label">
                              <Printer className="w-4 h-4" />
                            </Button>
                            {order.status === 'pending_payment' && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); handleConfirmPayment(order); }}
                                title="Confirm Payment"
                                className="text-green-400 hover:text-green-300 hover:bg-green-400/10"
                              >
                                <span className="font-bold">₹</span>
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPage = Math.max(1, page - 1);
                  setPage(newPage);
                  fetchData(newPage);
                }}
                disabled={page <= 1}
                className="border-slate-700 hover:bg-slate-800"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPage = Math.min(totalPages, page + 1);
                  setPage(newPage);
                  fetchData(newPage);
                }}
                disabled={page >= totalPages}
                className="border-slate-700 hover:bg-slate-800"
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Order Detail Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle>Order #{selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Items - with returned quantities subtracted */}
              {(() => {
                // Collect all returned quantities from pos_return notes
                const returnedQtys = {};
                (selectedOrder.notes || []).forEach(note => {
                  if (note?.type === 'pos_return') {
                    (note.items || []).forEach(ri => {
                      const pid = ri.product_id || ri.id;
                      returnedQtys[pid] = (returnedQtys[pid] || 0) + (ri.quantity || 0);
                    });
                  }
                });

                const effectiveItems = selectedOrder.items?.map(item => {
                  const pid = item.product_id || item.id;
                  const returned = returnedQtys[pid] || 0;
                  const netQty = Math.max(0, (item.quantity || 0) - returned);
                  const unitPrice = item.price || 0;
                  const unitTotal = item.total ? item.total / (item.quantity || 1) : unitPrice;
                  const netTotal = unitTotal * netQty;
                  return { ...item, netQty, netTotal, returned };
                }).filter(item => item.netQty > 0);

                const effectiveTotal = effectiveItems?.reduce((s, i) => s + i.netTotal, 0) || 0;
                const hasReturns = Object.keys(returnedQtys).length > 0;

                return (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      Items
                      {hasReturns && <span className="text-xs text-amber-400 font-normal">(after returns)</span>}
                    </h4>
                    <div className="space-y-2">
                      {effectiveItems?.map((item, index) => (
                        <div key={index} className="flex justify-between p-3 bg-slate-700/50 rounded-lg">
                          <div>
                            <p className="font-medium">{item.product_name || 'Unknown Product'}</p>
                            <p className="text-sm text-slate-400">
                              SKU: {item.sku || 'N/A'} | Qty: {item.netQty}
                              {item.returned > 0 && <span className="text-amber-400 ml-1">(returned: {item.returned})</span>}
                              {' '}| Unit: ₹{(item.price || 0).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">₹{item.netTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-700 flex justify-between font-bold">
                      <span>Total</span>
                      <span>₹{hasReturns ? effectiveTotal.toLocaleString(undefined, { maximumFractionDigits: 2 }) : selectedOrder.grand_total.toLocaleString()}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Shipping Address */}
              <div>
                <h4 className="font-semibold mb-3">Shipping Address</h4>
                <div className="p-3 bg-slate-700/50 rounded-lg text-sm">
                  <p className="font-medium">{selectedOrder.shipping_address?.name || 'N/A'}</p>
                  <p className="text-slate-400">{selectedOrder.shipping_address?.line1 || ''}</p>
                  {selectedOrder.shipping_address?.line2 && <p className="text-slate-400">{selectedOrder.shipping_address.line2}</p>}
                  <p className="text-slate-400">
                    {selectedOrder.shipping_address?.city || ''}, {selectedOrder.shipping_address?.state || ''} - {selectedOrder.shipping_address?.pincode || ''}
                  </p>
                  <p className="text-slate-400">Phone: {selectedOrder.shipping_address?.phone || selectedOrder.customer_phone || 'N/A'}</p>
                </div>
              </div>

              {(selectedOrder.notes?.some(n => n.type === 'pos_return') ||
                selectedOrder.tracking_history?.some(h => h.status === 'returned')) && (
                  <div>
                    <h4 className="font-semibold mb-3 text-orange-400 flex items-center gap-2">
                      <RefreshCcw className="w-4 h-4" />
                      Return History
                    </h4>
                    <div className="space-y-4">
                      {/* POS Returns */}
                      {selectedOrder.notes?.filter(n => n.type === 'pos_return').map((note, idx) => (
                        <div key={`pos-${idx}`} className="bg-slate-700/50 rounded-lg p-3 border border-orange-500/20">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-400">
                              {new Date(note.date).toLocaleString('en-IN')}
                            </span>
                            <span className="text-slate-400">
                              Processed by: {note.processed_by || 'Unknown'}
                            </span>
                          </div>
                          <div className="space-y-2">
                            {note.items.map((item, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span>
                                  {item.product_name || `Product #${item.product_id}`}
                                  <span className="text-slate-500 ml-2">x {item.quantity}</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {/* Online/Standard Returns (from Tracking History) */}
                      {selectedOrder.tracking_history?.filter(h => h.status === 'returned').map((h, i) => (
                        <div key={`track-${i}`} className="bg-slate-700/50 rounded-lg p-3 border border-orange-500/20">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-400">
                              {new Date(h.timestamp || h.updated_at || h.date).toLocaleString('en-IN')}
                            </span>
                            <span className="text-slate-400">
                              Updated by: {h.updated_by || 'System'}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-orange-200">Standard Return</p>
                          <p className="text-sm text-slate-300 mt-1">{h.notes}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Update Status */}
              <div className="space-y-4 pt-4 border-t border-slate-700">
                <h4 className="font-semibold">Update Order Status</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={updateData.status} onValueChange={(v) => setUpdateData({ ...updateData, status: v })}>
                      <SelectTrigger className="input-admin">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="pending_payment">Payment Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Courier Provider</Label>
                    <Select value={updateData.courier_provider} onValueChange={(v) => setUpdateData({ ...updateData, courier_provider: v })}>
                      <SelectTrigger className="input-admin">
                        <SelectValue placeholder="Select courier" />
                      </SelectTrigger>
                      <SelectContent>
                        {couriers.map((c) => (
                          <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tracking Number</Label>
                  <Input
                    value={updateData.tracking_number}
                    onChange={(e) => setUpdateData({ ...updateData, tracking_number: e.target.value })}
                    placeholder="Enter tracking number"
                    className="input-admin"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={updateData.notes}
                    onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                    placeholder="Add a note..."
                    className="input-admin"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
                  <Button onClick={handleUpdateStatus} className="bg-primary hover:bg-primary/90">
                    Update Order
                  </Button>
                </div>
              </div>
            </div>
          )
          }
        </DialogContent >
      </Dialog >

      {/* Shipping Label Dialog */}
      < Dialog open={showLabelDialog} onOpenChange={setShowLabelDialog} >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-black">Shipping Label - {labelOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {labelOrder && (
            <div>
              <div ref={labelRef}>
                <ShippingLabel order={labelOrder} settings={settings} />
              </div>
              <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
                <Button variant="ghost" onClick={() => setShowLabelDialog(false)}>Close</Button>
                <Button onClick={handlePrintShippingLabel} className="bg-primary hover:bg-primary/90">
                  <Printer className="w-4 h-4 mr-2" />
                  Print Label
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog >
    </div >
  );
}
